import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import map from 'lodash/map';

import {
  useGetJobApplications,
  useUpdateJobFavourite,
} from 'src/pages/ProjectDetailsPage/api';

import DataTable from 'src/components/DataTable';
import StudentLink from 'src/components/StudentLink';

import messages from '../../messages';
import { useStyles } from '../../JobDetails.styled';
import FavoriteButton from '../FavoriteButton/FavoriteButton';

export interface JobApplicationTabProps {
  jobUuid: string;
  job?: UCM.JobType | null;
  filters: Record<string, string>;
  onCandidateSelect?: (item: UCM.JobApplicationType | string | null) => void;
  candidate: UCM.JobApplicationType | null;
}

export default function JobApplicationsAll({
  jobUuid,
  filters,
}: JobApplicationTabProps) {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const [params, setParams] = useState<Record<string, string | number>>({
    page: 1,
  });

  const { data: jobApplications, isLoading } = useGetJobApplications({
    jobUuid,
    type: 'all',
    filters,
    params,
    enabled: Boolean(jobUuid),
  });

  const { mutate: updateJobFavorite, isPending: isFavoritePending } =
    useUpdateJobFavourite();

  const handleFavorite = useCallback(
    (favorite: boolean, uuid: string) => {
      if (isFavoritePending) return;

      void updateJobFavorite({ jobUuid, applicationUuid: uuid, favorite });
    },
    [jobUuid, isFavoritePending],
  );

  const handlePagination = useCallback((_: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  const columns: Array<{
    id: keyof UCM.JobApplicationType | string;
    label: string;
  }> = useMemo(
    () => [
      {
        id: 'applicant',
        label: formatMessage(messages.applicantLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant) return '-';
          const { id, fullName, uuid } = item.applicant;
          return (
            <FavoriteButton
              handleChange={(active) => handleFavorite(active, item.uuid)}
              active={item.favoriteManager}
            >
              <StudentLink label={`${id} - ${fullName}`} uuid={uuid} />
            </FavoriteButton>
          );
        },
      },
      {
        id: 'appliedCount',
        label: formatMessage(messages.appliedToLabel),
        render: (appliedCount: number | null) => (
          <Typography variant="inherit">{appliedCount ?? '-'}</Typography>
        ),
        align: 'center',
      },
      {
        id: 'yearlyWorkdaysCount',
        label: formatMessage(messages.workdayLabel),
        render: (yearlyWorkdaysCount: number) => (
          <Typography variant="inherit">{yearlyWorkdaysCount}</Typography>
        ),
        align: 'center',
      },
      {
        id: 'initialScore',
        label: formatMessage(messages.initialScoreColumnLabel),
        size: 'small',
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.initialRatingScore) return '-';
          return (
            <Typography variant="inherit">
              {item?.applicant.initialRatingScore}
            </Typography>
          );
        },
        align: 'center',
      },

      {
        id: 'ratingScore',
        label: formatMessage(messages.ratingLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.ratingScore) return '-';
          return (
            <Typography variant="inherit">
              {item.applicant.ratingScore}
            </Typography>
          );
        },
        align: 'center',
      },
      {
        id: 'prescreeningRating',
        label: formatMessage(messages.preScreenLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          const { prescreeningRating } = item.applicant;

          return (
            <Tooltip
              arrow
              title={
                <Stack spacing={1}>
                  {Object.entries(prescreeningRating).map(([key, value]) => (
                    <Typography
                      key={key}
                      variant="inherit"
                      textTransform="capitalize"
                    >
                      {key}: {value}
                    </Typography>
                  ))}
                </Stack>
              }
            >
              <Typography variant="inherit">
                {prescreeningRating.average}
              </Typography>
            </Tooltip>
          );
        },
        align: 'center',
      },
      {
        id: 'profile',
        label: formatMessage(messages.profileLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant?.status) return '-';
          let label = formatMessage(messages.completeLabel);
          if (item.applicant?.status?.code === 'required')
            label = formatMessage(messages.incompleteLabel);
          return <Typography variant="inherit">{label}</Typography>;
        },
        align: 'center',
      },
      {
        id: 'statuses',
        label: formatMessage(messages.statusLabel),
        render: (_: unknown, item: UCM.JobApplicationType) => {
          if (!item.statuses) return '-';
          return map(item?.statuses, (status) => {
            const isWaitlisted =
              status.isWaitlisted && status.status === 'accepted';
            return (
              <Typography
                key={status.status}
                variant="inherit"
                textTransform="capitalize"
              >
                {isWaitlisted
                  ? formatMessage(messages.waitlistedLabel)
                  : status.status}
              </Typography>
            );
          });
        },
        align: 'center',
      },
    ],
    [],
  );

  return (
    <Grid size={{ xs: 12 }}>
      <DataTable
        rows={jobApplications?.data}
        columns={columns}
        loading={isLoading}
        className={classes.tableContainer}
        onPaginate={handlePagination}
        pagination={jobApplications?.meta}
      />
    </Grid>
  );
}
